/**
 * generateBomHtml — Generates a print-ready Bill of Materials HTML document
 * from a project's preflight resource items.
 *
 * Opens a new window with a formatted BOM grouped by phase and category,
 * then triggers the browser's native print dialog (print to PDF or printer).
 *
 * Columns are optimised for a physical shopping/procurement printout:
 *   ☐  |  Item  |  Qty (need / have)  |  Unit Cost  |  Est. Total  |  Status  |  Notes
 */

import type { PreflightItem, PreflightSummary, ResourcePhase, ResourceCategory } from './preflightTypes';
import { RESOURCE_PHASES, RESOURCE_CATEGORIES, computePreflightSummary, normalizePhase, normalizeCategory } from './preflightTypes';
import i18n from '../../i18n/config';

/* Cast to bypass i18next v25 strict 0-arg type signature */
const t = i18n.t.bind(i18n) as (key: string, opts?: Record<string, string | number>) => string;

/* ── Phase / category display helpers ─────────────────────────────── */

function phaseLabel(phase: ResourcePhase): string {
  return t(`kpi:preflight.phases.${phase}`);
}

function categoryLabel(cat: ResourceCategory): string {
  return t(`kpi:preflight.categories.${cat}`);
}

/* ── Currency formatting ──────────────────────────────────────────── */

function fmtCurrency(value: number | null | undefined, currency?: string | null): string {
  if (value == null) return '—';
  try {
    return new Intl.NumberFormat(i18n.language, {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${(currency || 'USD')} ${value.toFixed(2)}`;
  }
}

/* ── Status helpers ───────────────────────────────────────────────── */

function statusText(item: PreflightItem): string {
  if (item.is_fulfilled) return t('kpi:preflight.fulfilled_label');
  if (item.requirement_type === 'threshold' && item.target_quantity) {
    return `${item.current_quantity} / ${item.target_quantity}${item.unit ? ' ' + item.unit : ''}`;
  }
  return t('kpi:preflight.pending_label');
}

function statusClass(item: PreflightItem): string {
  return item.is_fulfilled ? 'fulfilled' : 'pending';
}

/* ── Computed totals (mirrors BOM rollup logic in BudgetTab) ─────── */

function computeEstTotal(item: PreflightItem): number {
  if (item.estimated_total != null) return item.estimated_total;
  if (item.unit_cost != null && item.target_quantity != null) {
    return item.unit_cost * item.target_quantity;
  }
  return 0;
}

function computeActTotal(item: PreflightItem): number {
  if (item.actual_total != null) return item.actual_total;
  if (item.actual_cost != null) {
    return item.actual_cost * item.current_quantity;
  }
  return 0;
}

/* ── Notes column: merge description + cost_note ─────────────────── */

function notesText(item: PreflightItem): string {
  const parts: string[] = [];
  if (item.cost_note) parts.push(item.cost_note);
  if (item.description) parts.push(item.description);
  return parts.join(' · ') || '—';
}

/* ── Escape HTML ──────────────────────────────────────────────────── */

function esc(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/* ── Main generator ───────────────────────────────────────────────── */

export function openBomPrintWindow(
  items: PreflightItem[],
  projectName: string,
  budgetCurrency?: string,
): void {
  const activeItems = items.filter((i) => i.state !== 'withdrawn');
  const summary = computePreflightSummary(items);
  const currency = budgetCurrency || 'USD';
  const now = new Date();
  const dateStr = now.toLocaleDateString(i18n.language, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  /* Group items: phase → category → items[]
   * Use normalizePhase / normalizeCategory to handle legacy values,
   * and prefer cost_category (the budget-facing field) over category. */
  const phaseMap = new Map<ResourcePhase, Map<ResourceCategory, PreflightItem[]>>();
  for (const phase of RESOURCE_PHASES) {
    const catMap = new Map<ResourceCategory, PreflightItem[]>();
    for (const cat of RESOURCE_CATEGORIES) {
      const matching = activeItems
        .filter((i) => normalizePhase(i.phase) === phase && normalizeCategory(i.category) === cat)
        .sort((a, b) => a.sort_order - b.sort_order);
      if (matching.length > 0) catMap.set(cat, matching);
    }
    if (catMap.size > 0) phaseMap.set(phase, catMap);
  }

  /* ── Compute grand totals for summary strip ────────────────────── */
  let grandEstimated = 0;
  let grandActual = 0;
  for (const item of activeItems) {
    grandEstimated += computeEstTotal(item);
    grandActual += computeActTotal(item);
  }

  /* ── Column headers (i18n) ─────────────────────────────────────── */
  const colCheck  = '☐';
  const colItem   = esc(t('kpi:preflight.bomColItem'));
  const colQty    = esc(t('kpi:preflight.bomColQty'));
  const colUnit   = esc(t('kpi:preflight.bomColUnitCost'));
  const colEst    = esc(t('kpi:preflight.bomColEstTotal'));
  const colStatus = esc(t('kpi:preflight.bomColStatus'));
  const colNotes  = esc(t('kpi:preflight.bomColNotes'));

  /* ── Build HTML ─────────────────────────────────────────────────── */

  let phaseSectionsHtml = '';

  /* Use Array.from() to avoid downlevelIteration requirement on Map */
  Array.from(phaseMap.entries()).forEach(([phase, catMap]) => {
    const phaseSummary = summary.by_phase[phase];
    const phasePct = phaseSummary.total > 0
      ? Math.round((phaseSummary.fulfilled / phaseSummary.total) * 100)
      : 100;

    let categoryTablesHtml = '';

    Array.from(catMap.entries()).forEach(([cat, catItems]) => {
      const catFulfilled = catItems.filter((i) => i.is_fulfilled).length;
      let catEstimated = 0;
      let catActual = 0;

      let rowsHtml = '';
      catItems.forEach((item) => {
        const est = computeEstTotal(item);
        const act = computeActTotal(item);
        catEstimated += est;
        catActual += act;

        const qtyCell = item.target_quantity != null
          ? `${item.current_quantity} / ${item.target_quantity}${item.unit ? ' ' + esc(item.unit) : ''}`
          : '—';

        rowsHtml += `
          <tr>
            <td class="check-col">☐</td>
            <td class="item-col">${esc(item.label)}</td>
            <td class="qty-col">${qtyCell}</td>
            <td class="num-col">${item.unit_cost != null ? fmtCurrency(item.unit_cost, item.cost_currency || currency) : '—'}</td>
            <td class="num-col">${est > 0 ? fmtCurrency(est, item.cost_currency || currency) : '—'}</td>
            <td class="status-col ${statusClass(item)}">${esc(statusText(item))}</td>
            <td class="notes-col">${esc(notesText(item))}</td>
          </tr>`;
      });

      /* Category subtotal row */
      rowsHtml += `
        <tr class="subtotal-row">
          <td></td>
          <td colspan="3" class="subtotal-label">${esc(t('kpi:preflight.bomSubtotal'))}</td>
          <td class="num-col">${fmtCurrency(catEstimated, currency)}</td>
          <td></td>
          <td></td>
        </tr>`;

      categoryTablesHtml += `
        <div class="category-section">
          <h3>${esc(categoryLabel(cat))}
            <span class="cat-count">${catFulfilled} / ${catItems.length}</span>
          </h3>
          <table>
            <thead>
              <tr>
                <th class="check-col">${colCheck}</th>
                <th class="item-col">${colItem}</th>
                <th class="qty-col">${colQty}</th>
                <th class="num-col">${colUnit}</th>
                <th class="num-col">${colEst}</th>
                <th class="status-col">${colStatus}</th>
                <th class="notes-col">${colNotes}</th>
              </tr>
            </thead>
            <tbody>
              ${rowsHtml}
            </tbody>
          </table>
        </div>`;
    });

    phaseSectionsHtml += `
      <div class="phase-section">
        <h2>${esc(phaseLabel(phase))}
          <span class="phase-count">${phaseSummary.fulfilled} / ${phaseSummary.total} (${phasePct}%)</span>
        </h2>
        ${categoryTablesHtml}
      </div>`;
  });

  const html = `<!DOCTYPE html>
<html lang="${i18n.language}" dir="${i18n.dir()}">
<head>
  <meta charset="utf-8">
  <title>${esc(t('kpi:preflight.bomTitle', { project: projectName }))}</title>
  <style>
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 10pt;
      line-height: 1.4;
      color: #1a1a1a;
      padding: 20px 24px;
    }

    /* ── Header ── */
    .bom-header {
      border-bottom: 3px solid #1a1a1a;
      padding-bottom: 12px;
      margin-bottom: 16px;
    }
    .bom-header h1 {
      font-size: 16pt;
      font-weight: 700;
      margin-bottom: 2px;
    }
    .bom-header .project-name {
      font-size: 12pt;
      font-weight: 500;
      color: #444;
    }
    .bom-meta {
      display: flex;
      justify-content: space-between;
      margin-top: 6px;
      font-size: 8.5pt;
      color: #666;
    }

    /* ── Summary strip ── */
    .summary-strip {
      display: flex;
      gap: 20px;
      flex-wrap: wrap;
      background: #f5f5f5;
      border-radius: 5px;
      padding: 10px 14px;
      margin-bottom: 18px;
      font-size: 9pt;
    }
    .summary-strip .stat { font-weight: 600; }
    .summary-strip .stat-label { color: #666; margin-inline-end: 3px; }

    /* ── Phase sections ── */
    .phase-section {
      margin-bottom: 20px;
      page-break-inside: avoid;
    }
    .phase-section h2 {
      font-size: 12pt;
      font-weight: 700;
      border-bottom: 1.5px solid #ccc;
      padding-bottom: 3px;
      margin-bottom: 10px;
      display: flex;
      align-items: baseline;
      gap: 8px;
    }
    .phase-count {
      font-size: 8.5pt;
      font-weight: 400;
      color: #888;
    }

    /* ── Category subsections ── */
    .category-section {
      margin-bottom: 14px;
      page-break-inside: avoid;
    }
    .category-section h3 {
      font-size: 10pt;
      font-weight: 600;
      margin-bottom: 4px;
      display: flex;
      align-items: baseline;
      gap: 6px;
    }
    .cat-count {
      font-size: 8.5pt;
      font-weight: 400;
      color: #999;
    }

    /* ── Tables ── */
    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 9pt;
      margin-bottom: 6px;
      table-layout: fixed;
    }
    thead th {
      background: #f0f0f0;
      text-align: start;
      font-weight: 600;
      padding: 5px 6px;
      border-bottom: 1.5px solid #bbb;
      white-space: nowrap;
      overflow: hidden;
    }
    tbody td {
      padding: 4px 6px;
      border-bottom: 1px solid #e5e5e5;
      vertical-align: top;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    tbody tr:last-child td { border-bottom: none; }

    /* ── Column widths ── */
    .check-col  { width: 28px; text-align: center; }
    .item-col   { width: 20%; }
    .qty-col    { width: 10%; text-align: end; white-space: nowrap; }
    .num-col    { width: 11%; text-align: end; font-variant-numeric: tabular-nums; white-space: nowrap; }
    .status-col { width: 10%; white-space: nowrap; }
    .notes-col  { width: auto; font-size: 8.5pt; color: #555; }

    /* ── Subtotal rows ── */
    .subtotal-row td {
      border-top: 1.5px solid #ccc;
      border-bottom: none;
      font-weight: 600;
      font-size: 8.5pt;
      padding-top: 4px;
    }
    .subtotal-label {
      text-align: end;
      padding-inline-end: 6px;
    }

    /* ── Grand total ── */
    .grand-total {
      margin-top: 20px;
      border-top: 2px solid #1a1a1a;
      padding-top: 10px;
      display: flex;
      gap: 32px;
      font-size: 10pt;
      font-weight: 700;
    }
    .grand-total .label { color: #666; font-weight: 500; margin-inline-end: 4px; }

    /* ── Status badges ── */
    .fulfilled {
      color: #16a34a;
      font-weight: 600;
    }
    .pending {
      color: #dc2626;
    }

    /* ── Print overrides ── */
    @media print {
      body { padding: 0; font-size: 9pt; }
      .no-print { display: none !important; }
      .phase-section { page-break-inside: avoid; }
      .category-section { page-break-inside: avoid; }
      .summary-strip { background: #f5f5f5 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      thead th { background: #f0f0f0 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .check-col { width: 24px; }
    }

    /* ── Print button ── */
    .print-bar {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      background: #1a1a1a;
      color: #fff;
      padding: 8px 24px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      z-index: 9999;
    }
    .print-bar button {
      background: #3b82f6;
      color: #fff;
      border: none;
      border-radius: 6px;
      padding: 6px 18px;
      font-size: 10pt;
      cursor: pointer;
      font-weight: 600;
    }
    .print-bar button:hover { background: #2563eb; }
    .print-bar-spacer { height: 48px; }
  </style>
</head>
<body>
  <div class="print-bar no-print">
    <span>${esc(t('kpi:preflight.bomTitle', { project: projectName }))}</span>
    <button onclick="window.print()">${esc(t('kpi:preflight.bomPrint'))}</button>
  </div>
  <div class="print-bar-spacer no-print"></div>

  <div class="bom-header">
    <h1>${esc(t('kpi:preflight.bomHeading'))}</h1>
    <div class="project-name">${esc(projectName)}</div>
    <div class="bom-meta">
      <span>${esc(dateStr)}</span>
      <span>${esc(t('kpi:preflight.readiness'))}: ${summary.readiness_pct}%</span>
    </div>
  </div>

  <div class="summary-strip">
    <div>
      <span class="stat-label">${esc(t('kpi:preflight.bomTotalItems'))}:</span>
      <span class="stat">${summary.total_items}</span>
    </div>
    <div>
      <span class="stat-label">${esc(t('kpi:preflight.fulfilled_label'))}:</span>
      <span class="stat">${summary.fulfilled_items}</span>
    </div>
    <div>
      <span class="stat-label">${esc(t('kpi:preflight.pending_label'))}:</span>
      <span class="stat">${summary.total_items - summary.fulfilled_items}</span>
    </div>
    <div>
      <span class="stat-label">${esc(t('kpi:preflight.bomColEstTotal'))}:</span>
      <span class="stat">${fmtCurrency(grandEstimated, currency)}</span>
    </div>
    <div>
      <span class="stat-label">${esc(t('kpi:preflight.bomActualSpend'))}:</span>
      <span class="stat">${fmtCurrency(grandActual, currency)}</span>
    </div>
  </div>

  ${phaseSectionsHtml}

  <div class="grand-total">
    <div>
      <span class="label">${esc(t('kpi:preflight.bomColEstTotal'))}:</span>
      ${fmtCurrency(grandEstimated, currency)}
    </div>
    <div>
      <span class="label">${esc(t('kpi:preflight.bomActualSpend'))}:</span>
      ${fmtCurrency(grandActual, currency)}
    </div>
  </div>

  <div class="bom-footer" style="margin-top: 24px; border-top: 1px solid #ccc; padding-top: 6px; font-size: 7.5pt; color: #999; text-align: center;">
    ${esc(t('kpi:preflight.bomFooter'))} — ${esc(dateStr)}
  </div>
</body>
</html>`;

  /* ── Open window & trigger print ────────────────────────────────── */
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    // Popup blocker — fall back to blob download
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `BOM-${projectName.replace(/[^a-zA-Z0-9]/g, '_')}.html`;
    a.click();
    URL.revokeObjectURL(url);
    return;
  }
  printWindow.document.write(html);
  printWindow.document.close();
}
